/* eslint-disable @typescript-eslint/ban-types */
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/solid'
import Column from 'components/atoms/column'
import Text from 'components/atoms/text'
import { classNameBuilder } from 'helpers/class-name-builder'
import { uniqueId } from 'lodash'
import React from 'react'
import {
  ActionMeta,
  components,
  default as ReactSelect,
  DropdownIndicatorProps,
  StylesConfig,
} from 'react-select'

type ConditionalRequiredGetOptionValue<T> = T extends
  | unknown
  | { value: string | number }
  ? {}
  : { getOptionValue: (option: T) => string }

type ConditionalRequiredGetOptionLabel<T> = T extends
  | unknown
  | { label: string }
  ? {}
  : { getOptionLabel: (option: T) => string }

export type SelectInputProps<T> = {
  id?: string
  name?: string
  label?: string
  error?: string
  required?: boolean
  className?: string
  placeholder?: string
  value?: T
  defaultValue?: T
  options?: T[]
  isSearchable?: boolean
  isClearable?: boolean
  onChange?: (newValue: T, actionMeta: ActionMeta<T>) => void
  onBlur?: React.FocusEventHandler<HTMLInputElement>
  getOptionValue?: (option: T) => string
  getOptionLabel?: (option: T) => string
  isOptionDisabled?: (option: T, selectValue: T[]) => boolean
} & ConditionalRequiredGetOptionValue<T> &
  ConditionalRequiredGetOptionLabel<T>

const SelectInput = <T,>(props: SelectInputProps<T>): React.ReactElement => {
  const {
    id = uniqueId('select-input-'),
    className,
    label,
    error,
    required,
    ...SelectProps
  } = props
  const customStyles: StylesConfig = {
    control: (provided) =>
      error
        ? {
            ...provided,
            border: '2px solid rgb(225, 108, 118, 1)',
            backgroundColor: 'rgb(250, 234, 235, 0.25)',
          }
        : { ...provided },
  }
  return (
    <Column className={classNameBuilder('w-full', className)}>
      <label
        htmlFor={id}
        className={classNameBuilder(
          'block text-sm font-medium text-gray-700',
          required && 'after:ml-0.5 after:text-red-300 after:content-["*"]'
        )}
      >
        {label}
      </label>
      <ReactSelect
        {...SelectProps}
        styles={customStyles}
        className={classNameBuilder(label && 'mt-1')}
        theme={(theme) => ({ ...theme, borderRadius: 6 })}
        components={{ DropdownIndicator, IndicatorSeparator: null }}
        // menuPosition="absolute"
      />
      <Text className="h-0 text-xs text-red-400">{error}</Text>
    </Column>
  )
}

const DropdownIndicator = (props: DropdownIndicatorProps) => {
  return (
    <components.DropdownIndicator className="mr-1 flex flex-col" {...props}>
      <ChevronUpIcon className="-mb-1 h-sm w-sm text-indigo-300" />
      <ChevronDownIcon className="-mt-1 h-sm w-sm text-indigo-300" />
    </components.DropdownIndicator>
  )
}

export default SelectInput
